import { EnterForm } from '../components/EnterForm';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboard, createUserTransfer, getAllUsersBasic } from '../lib/api';
import { formatCurrency, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DateInput } from '../components/ui/date-input';
import { SearchableSelect } from '../components/ui/searchable-select';
import { 
  Wallet, TrendingUp, TrendingDown,
  Plus, ArrowLeftRight, ShoppingCart, AlertCircle, Clock, CreditCard, BookOpen, Users
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [transferForm, setTransferForm] = useState({
    from_user_id: user?.id || '',
    to_user_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        getDashboard(),
        getAllUsersBasic()
      ]);
      setData(dashboardRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setError('');

    if (transferForm.from_user_id === transferForm.to_user_id) {
      setError('Cannot transfer to the same user');
      return;
    }

    setSubmitting(true);
    try {
      await createUserTransfer({
        from_user_id: transferForm.from_user_id,
        to_user_id: transferForm.to_user_id,
        amount: parseFloat(transferForm.amount),
        date: transferForm.date,
        remarks: transferForm.remarks
      });
      toast.success('Transfer completed successfully');
      setTransferDialogOpen(false);
      setTransferForm({ from_user_id: user?.id || '', to_user_id: '', amount: '', date: new Date().toISOString().split('T')[0], remarks: '' });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.name
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Calculate total from user balances
  const totalUserBalance = data?.user_balances?.reduce((sum, u) => sum + (u.cash_balance || 0), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {user?.name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && data?.pending_approvals > 0 && (
            <Badge 
              variant="destructive" 
              className="flex items-center gap-1 w-fit cursor-pointer hover:bg-destructive/90"
              onClick={() => navigate('/recurring', { state: { tab: 'pending' } })}
              data-testid="pending-approvals-badge"
            >
              <AlertCircle className="h-3 w-3" />
              {data.pending_approvals} pending approval{data.pending_approvals > 1 ? 's' : ''}
            </Badge>
          )}
          {(data?.entries_due_to_post || 0) > 0 && (
            <Badge 
              variant="warning" 
              className="flex items-center gap-1 w-fit cursor-pointer hover:opacity-90 bg-amber-500 text-white"
              onClick={() => navigate('/recurring', { state: { tab: 'approved' } })}
              data-testid="entries-due-badge"
            >
              <Clock className="h-3 w-3" />
              {data.entries_due_to_post} due to post
            </Badge>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/income', { state: { openDialog: true } })}
              className="gap-2"
              data-testid="quick-add-income"
            >
              <TrendingUp className="h-4 w-4" />
              Add Income
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/expense', { state: { openDialog: true } })}
              className="gap-2"
              data-testid="quick-add-expense"
            >
              <TrendingDown className="h-4 w-4" />
              Add Expense
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/sales', { state: { openDialog: true } })}
              className="gap-2"
              data-testid="quick-add-sale"
            >
              <ShoppingCart className="h-4 w-4" />
              Add Sale
            </Button>
            <Button 
              variant="outline"
              onClick={() => setTransferDialogOpen(true)}
              className="gap-2"
              data-testid="quick-add-transfer"
            >
              <ArrowLeftRight className="h-4 w-4" />
              Transfer Balance
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Row 1: Company Cash Balance + User Balances */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid="total-company-balance" className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate('/ledger', { state: { tab: 'user-ledger' } })}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Company Cash Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono ${totalUserBalance >= 0 ? 'text-income' : 'text-expense'}`}>
              {formatCurrency(totalUserBalance)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total of all user balances</p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              User Balances
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {data?.user_balances?.map(u => (
                <Card
                  key={u.id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors border"
                  onClick={() => navigate('/ledger', { state: { tab: 'user-ledger', userId: u.id } })}
                  data-testid={`user-balance-card-${u.username}`}
                >
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{u.name}</p>
                    <p className={`text-xl font-bold font-mono ${u.cash_balance >= 0 ? '' : 'text-expense'}`}>{formatCurrency(u.cash_balance)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Customer Outstanding + Entry Ledger Receivable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card data-testid="total-outstanding-card" className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate('/sales', { state: { tab: 'outstanding' } })}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Customer Outstanding</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-10">
              <div>
                <div className="text-3xl font-bold font-mono text-expense">{formatCurrency(data?.total_outstanding || 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">Total Outstanding</p>
              </div>
              {(data?.total_overdue || 0) > 0 && (
                <div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-5 w-5 text-destructive" />
                    <span className="text-3xl font-bold font-mono text-destructive">{formatCurrency(data.total_overdue)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Overdue</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="entry-ledger-receivables-card" className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate('/ledger', { state: { tab: 'entry-ledger' } })}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Entry Ledger Receivable</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-expense">
              {formatCurrency(data?.total_entry_ledger_receivable || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Across {data?.entry_ledger_receivables?.length || 0} ledgers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Income by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(data?.income_by_category || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(data?.income_by_category || {}).map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center">
                    <span className="text-sm">{category}</span>
                    <span className="text-sm font-mono text-income">{formatCurrency(amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No income data yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Expense by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(data?.expense_by_category || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(data?.expense_by_category || {}).map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center">
                    <span className="text-sm">{category}</span>
                    <span className="text-sm font-mono text-expense">{formatCurrency(amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No expense data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Between Users</DialogTitle>
          </DialogHeader>
          <EnterForm onSubmit={handleTransfer} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>From User</Label>
              <SearchableSelect
                options={userOptions}
                value={transferForm.from_user_id}
                onChange={(value) => setTransferForm({ ...transferForm, from_user_id: value })}
                placeholder="Select user"
                searchPlaceholder="Search user..."
                data-testid="transfer-from-select"
              />
            </div>

            <div className="flex justify-center">
              <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
            </div>

            <div className="space-y-2">
              <Label>To User</Label>
              <SearchableSelect
                options={userOptions}
                value={transferForm.to_user_id}
                onChange={(value) => setTransferForm({ ...transferForm, to_user_id: value })}
                placeholder="Select user"
                searchPlaceholder="Search user..."
                data-testid="transfer-to-select"
              />
            </div>

            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                required
                data-testid="transfer-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <DateInput
                value={transferForm.date}
                onChange={(e) => setTransferForm({ ...transferForm, date: e.target.value })}
                required
                data-testid="transfer-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                placeholder="Optional remarks..."
                value={transferForm.remarks}
                onChange={(e) => setTransferForm({ ...transferForm, remarks: e.target.value })}
                data-testid="transfer-remarks-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !transferForm.from_user_id || !transferForm.to_user_id} 
                data-testid="transfer-submit"
              >
                {submitting ? 'Processing...' : 'Transfer'}
              </Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
